// Lightweight client-side profanity check for instant inline feedback.
// The backend (better-profanity + separator-tolerant matching) is the
// authoritative filter; this only powers the in-form warning.
// Catches spaces/symbols inserted between letters (e.g. "s h i t", "s.h.i.t")
// and common leetspeak, with word boundaries to avoid false positives
// like "assistant", "grass" or "class".

const CORE_BAD = [
  'anal', 'anus', 'arse', 'ass', 'asshole', 'bastard', 'bitch', 'bollock',
  'boner', 'bullshit', 'cock', 'cunt', 'dick', 'dildo', 'dyke', 'fag',
  'faggot', 'fuck', 'goddamn', 'handjob', 'homo', 'jizz', 'nigga', 'nigger',
  'piss', 'prick', 'pussy', 'queer', 'retard', 'shit', 'slut', 'spunk',
  'twat', 'wank', 'whore', 'cum',
];

const LEET = { '@': 'a', '4': 'a', '1': 'i', '!': 'i', '|': 'i', '3': 'e', '0': 'o', '$': 's', '5': 's', '7': 't' };

const escapeRe = (c) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const SEP = '[\\W_]*';
const PATTERNS = CORE_BAD.map(
  (w) => new RegExp('\\b' + w.split('').map(escapeRe).join(SEP) + '\\b', 'i')
);

const normalize = (s) => (s || '').toLowerCase().replace(/[@41!|30$57]/g, (ch) => LEET[ch] || ch);

export const containsProfanity = (text) => {
  if (!text) return false;
  const norm = normalize(text);
  return PATTERNS.some((p) => p.test(text) || p.test(norm));
};

// Reserved / authority usernames — only staff may use these.
const RESERVED = [
  'admin', 'admins', 'administrator', 'administrators', 'mod', 'mods',
  'moderator', 'moderators', 'owner', 'owners', 'superadmin', 'staff',
  'official', 'officials', 'curbalert', 'support', 'system', 'root',
  'security', 'team', 'authority', 'help', 'helpdesk',
];
export const isReservedUsername = (uname) => {
  const cleaned = (uname || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return RESERVED.some((w) => new RegExp('^' + w.replace(/[^a-z0-9]/g, '') + '[0-9]*$').test(cleaned));
};

// Strong profanity matched as a substring inside a username (no spaces), after
// stripping separators + leetspeak. Curated to avoid common false positives.
const USERNAME_BAD = [
  'fuck', 'shit', 'cunt', 'nigger', 'nigga', 'faggot', 'pussy', 'bitch',
  'whore', 'slut', 'retard', 'jizz', 'dildo', 'asshole', 'bullshit',
  'dipshit', 'motherfuck', 'blowjob', 'handjob', 'cumshot', 'rapist',
];
export const usernameHasProfanity = (uname) => {
  if (!uname) return false;
  if (containsProfanity(uname)) return true;
  const cleaned = uname.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/[@41!|30$57]/g, (ch) => LEET[ch] || ch);
  if (USERNAME_BAD.some((w) => cleaned.includes(w))) return true;
  // 'ass' is false-positive-prone, so guard with an allowlist of safe words.
  const ASS_SAFE = ['assist', 'class', 'grass', 'bass', 'lass', 'glass', 'mass', 'pass', 'brass', 'assess', 'assembl', 'embarrass', 'compass', 'molass', 'sass', 'crevasse', 'potass', 'wrasse'];
  if (cleaned.includes('ass') && !ASS_SAFE.some((s) => cleaned.includes(s))) return true;
  return false;
};
